/**
 * Planner API endpoints - itineraries, trips, AI planning
 */
import { fetchApi, API_BASE_URL, getCsrfToken } from './apiBase';
import { Itinerary, ItineraryItem, ItineraryListItem } from '../types';
import type { PublishStatus } from './plansApi';
import type { SurfaceWindow } from '../utils/sun';

// === Types ===

export interface TripSuggestion {
  prompt: string;
  label: string;
  tags?: string[];
}

// === AI Planning ===

/**
 * Poll for an async plan result using a signed token.
 * Retries with exponential backoff: 1s, 1.5s, 2.25s, ... max 8s.
 */
async function pollForResult<T>(
  token: string,
  maxAttempts: number = 20
): Promise<T> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const delay = Math.min(1000 * Math.pow(1.5, attempt), 8000);
    await new Promise((resolve) => setTimeout(resolve, delay));

    const url = `${API_BASE_URL}/api/planner/plan/status/?token=${encodeURIComponent(token)}`;
    const response = await fetch(url, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Polling error: ${response.status}`);
    }

    const data = await response.json();

    if (data.status === 'complete') {
      return data.result as T;
    }
    if (data.status === 'failed') {
      throw new Error(data.error || 'Plan creation failed');
    }
  }
  throw new Error('Plan creation timed out');
}

export async function createPlan(
  request: string,
  startDate?: string,
  endDate?: string
): Promise<Itinerary> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers['X-CSRFToken'] = csrfToken;
  }

  const response = await fetch(`${API_BASE_URL}/api/planner/plan/`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({
      request,
      date: startDate,
      end_date: endDate,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `API error: ${response.status}`);
  }

  // 201 = synchronous (eager mode) — return directly
  if (response.status === 201) {
    return response.json();
  }

  // 202 = async — poll for result
  if (response.status === 202) {
    const { token } = await response.json();
    return pollForResult<Itinerary>(token);
  }

  return response.json();
}

// === Next trip (Phase 4, spec §22.5) ===

export interface NextTripSummary {
  id: number;
  title: string;
  destination: string;
  start_date: string;
  days_until: number;
}

export interface ActiveTripSummary {
  id: number;
  title: string;
  destination: string;
  start_date: string | null;
  end_date: string;
  day_number: number;
  total_days: number;
}

export interface NextTripResponse {
  /** Which tab to open. The RULE lives on the server — see planner/next_trip.py.
   *  Three clients previously each decided "within seven days" for themselves and
   *  their day-difference helpers disagreed about timezones. */
  landing: 'now' | 'explore';
  is_traveling: boolean;
  active_trip: ActiveTripSummary | null;
  next_trip: NextTripSummary | null;
  /** Trip windows the sun resolver reads (Almanac S2). Never logged or sent to analytics. */
  surface_windows?: SurfaceWindow[];
}

/** Long enough for a bad connection, short enough that a hang is not forever. */
export const NEXT_TRIP_TIMEOUT_MS = 10000;

/**
 * The one answer to "what is my next trip", and where to land.
 *
 * **Bounded**, because `fetchApi` defaults to no timeout and this call decides
 * which tab the app opens on. A request that never settles strands that decision
 * for the whole mounted session, and there is deliberately no retry. The timeout
 * turns "hangs forever" into "lands on the default tab, and asks again next
 * session", which is the behaviour the deferred no-retry gap was accepted on.
 * Two indexed queries answer this endpoint; ten seconds is slack, not a budget.
 */
export async function getNextTrip(): Promise<NextTripResponse> {
  return fetchApi<NextTripResponse>('/api/planner/next-trip/', {}, NEXT_TRIP_TIMEOUT_MS);
}

// === Itineraries CRUD ===

export async function getItineraries(): Promise<ItineraryListItem[]> {
  const response = await fetchApi<{ itineraries: ItineraryListItem[] }>('/api/planner/itineraries/');
  return response.itineraries || [];
}

/** One page of the own-plans list with its envelope. `times_used` is the
 *  real uses of ALL the caller's plans (the unsliced total), so it is not
 *  the sum of the page's per-row counts. */
export interface ItinerariesPage {
  itineraries: ItineraryListItem[];
  times_used: number;
}

export async function getItinerariesPage(): Promise<ItinerariesPage> {
  const response = await fetchApi<Partial<ItinerariesPage>>('/api/planner/itineraries/');
  return {
    itineraries: response.itineraries || [],
    times_used: response.times_used ?? 0,
  };
}

export async function createItinerary(data: {
  title: string;
  description?: string;
  destination?: string;
  type?: string;
  start_date: string;
  end_date?: string;
}): Promise<Itinerary> {
  return fetchApi<Itinerary>('/api/planner/itineraries/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getItinerary(id: number): Promise<Itinerary> {
  return fetchApi<Itinerary>(`/api/planner/itineraries/${id}/`);
}

export async function getInvitedItinerary(inviteToken: string): Promise<Itinerary & { invite_token: string; invite_accepted: boolean }> {
  return fetchApi(`/api/planner/invited/${inviteToken}/`);
}

export async function updateItinerary(
  id: number,
  data: Partial<Itinerary>,
  expectedRevision: number,
): Promise<Itinerary> {
  return fetchApi<Itinerary>(`/api/planner/itineraries/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify({ ...data, expected_revision: expectedRevision }),
  });
}

export async function deleteItinerary(
  id: number,
  deleteEvents?: boolean
): Promise<{ success?: boolean; message?: string; events_deleted?: number }> {
  return fetchApi(`/api/planner/itineraries/${id}/`, {
    method: 'DELETE',
    body: deleteEvents !== undefined ? JSON.stringify({ delete_events: deleteEvents }) : undefined,
  });
}

export async function confirmItinerary(id: number): Promise<Itinerary> {
  return fetchApi<Itinerary>(`/api/planner/itineraries/${id}/confirm/`, {
    method: 'POST',
  });
}

// === Itinerary Items ===

export async function addItineraryItem(
  itineraryId: number,
  item: {
    name: string;
    type: string;
    date: string;
    time?: string;
    location?: string;
    address?: string;
    duration?: string;
    notes?: string;
  },
  expectedRevision: number,
): Promise<Itinerary> {
  return fetchApi<Itinerary>(`/api/planner/itineraries/${itineraryId}/items/`, {
    method: 'POST',
    body: JSON.stringify({ ...item, expected_revision: expectedRevision }),
  });
}

export async function updateItineraryItem(
  itineraryId: number,
  itemId: number,
  data: Partial<{
    name: string;
    type: string;
    date: string;
    time: string;
    location: string;
    address: string;
    duration: string;
    notes: string;
    order: number;
    /** The chat lock (slice 2). Server-side enforcement already refuses
     * AI edits to a confirmed item; this is how a user sets it. */
    confirmed: boolean;
  }>,
  expectedRevision: number,
): Promise<Itinerary> {
  return fetchApi<Itinerary>(`/api/planner/itineraries/${itineraryId}/items/${itemId}/`, {
    method: 'PATCH',
    body: JSON.stringify({ ...data, expected_revision: expectedRevision }),
  });
}

export async function checkinItineraryItem(
  itineraryId: number,
  itemId: number,
  status: 'completed' | 'skipped' | 'pending'
): Promise<Record<string, unknown>> {
  return fetchApi<Record<string, unknown>>(`/api/planner/itineraries/${itineraryId}/items/${itemId}/check-in/`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export async function updateVisibility(
  itineraryId: number,
  visibility: 'private' | 'public' | 'link'
): Promise<{ visibility: string; slug: string | null; share_url: string | null; publish?: PublishStatus }> {
  // The server already returns the COMPLETE publish status in this
  // acknowledgment; typing it lets the sheet render the outcome from the
  // write's own answer instead of a second GET that can fail (external round
  // 484, non-blocking 5).
  return fetchApi(`/api/planner/itineraries/${itineraryId}/visibility/`, {
    method: 'PATCH',
    body: JSON.stringify({ visibility }),
  });
}

/**
 * Fork an itinerary into the current account. `shareToken` is the authority
 * for a private plan reached through its share link — the same token that
 * opened the page — and is sent only in the request body, never in a URL.
 */
export async function forkItinerary(
  itineraryId: number,
  startDate: string,
  shareToken?: string
): Promise<Itinerary> {
  return fetchApi<Itinerary>(`/api/planner/itineraries/${itineraryId}/fork/`, {
    method: 'POST',
    body: JSON.stringify(
      shareToken ? { start_date: startDate, share_token: shareToken } : { start_date: startDate }
    ),
  });
}

export async function deleteItineraryItem(
  itineraryId: number,
  itemId: number,
  expectedRevision: number,
): Promise<Itinerary> {
  return fetchApi<Itinerary>(`/api/planner/itineraries/${itineraryId}/items/${itemId}/delete/`, {
    method: 'DELETE',
    body: JSON.stringify({ expected_revision: expectedRevision }),
  });
}

export async function reorderItineraryItems(
  itineraryId: number,
  itemIds: number[],
  expectedRevision: number,
): Promise<Itinerary> {
  return fetchApi<Itinerary>(`/api/planner/itineraries/${itineraryId}/items/reorder/`, {
    method: 'POST',
    body: JSON.stringify({ item_order: itemIds, expected_revision: expectedRevision }),
  });
}

// === Today Activities ===

export interface TodayActivity {
  id: number;
  name: string;
  time: string | null;
  time_raw: string | null;
  location: string;
  duration: string;
  notes: string;
  activity_status: string;
  is_favorite: boolean;
  outdoor: boolean;
  latitude: number | null;
  longitude: number | null;
  plan_name: string;
  plan_id: number;
  itinerary_id: number;
}

export async function getTodayActivities(date?: string): Promise<{ activities: TodayActivity[]; weather: DayWeather | null }> {
  const url = date ? `/api/planner/today/?date=${date}` : '/api/planner/today/';
  return fetchApi<{ activities: TodayActivity[]; weather: DayWeather | null }>(url);
}

// === Week Activities ===

export interface DayWeather {
  icon: string;
  high: number | null;
  low: number | null;
  description: string;
  source?: string;
}

export interface WeekDay {
  date: string;
  day_name: string;
  day_label: string;
  activity_count: number;
  activities: TodayActivity[];
  weather: DayWeather | null;
}

export async function getWeekActivities(start: string): Promise<{ start: string; days: WeekDay[] }> {
  return fetchApi<{ start: string; days: WeekDay[] }>(`/api/planner/week/?start=${start}`);
}

// === Month Summary ===

export interface MonthWeek {
  week_start: string;
  week_end: string;
  dates_label: string;
  activity_count: number;
  plans: string[];
  top_destination: string | null;
}

export interface MonthCluster {
  destination: string;
  activity_count: number;
  lat: number;
  lng: number;
}

export async function getMonthSummary(month: string): Promise<{ month: string; label: string; weeks: MonthWeek[]; clusters: MonthCluster[] }> {
  return fetchApi<{ month: string; label: string; weeks: MonthWeek[]; clusters: MonthCluster[] }>(`/api/planner/month/?month=${month}`);
}

// === Incoming Shares ===

export interface IncomingShare {
  type: 'fork_of_yours' | 'suggestion_accepted' | 'vote_invite' | 'recap_share' | 'invite_joined' | 'vote_digest';
  actor_name: string;
  actor_initials: string;
  plan_title: string;
  plan_id: number;
  timestamp: string;
}

export async function getIncomingShares(): Promise<{ shares: IncomingShare[] }> {
  return fetchApi<{ shares: IncomingShare[] }>('/api/planner/today/shares/');
}

// === Gap Suggestions ===

export interface GapSuggestion {
  type: string;
  start: string;
  end: string;
  duration: number;
  emoji: string;
  title: string;
  suggestion: string;
  suggestion_short: string;
  after_event?: string;
  before_event?: string;
}

export async function getTodayGaps(): Promise<{ gaps: GapSuggestion[] }> {
  return fetchApi<{ gaps: GapSuggestion[] }>('/api/planner/today/gaps/');
}

// === Favorites ===

export async function favoriteItineraryItem(
  itineraryId: number,
  itemId: number
): Promise<ItineraryItem> {
  return fetchApi<ItineraryItem>(`/api/planner/itineraries/${itineraryId}/items/${itemId}/favorite/`, {
    method: 'POST',
  });
}

// === Export ===

export async function exportItineraryEmail(
  itineraryId: number
): Promise<{ message: string }> {
  return fetchApi<{ message: string }>(`/api/planner/itineraries/${itineraryId}/export/`, {
    method: 'POST',
  });
}

// === Sharing ===

export async function getSharedItinerary(token: string): Promise<Itinerary> {
  return fetchApi<Itinerary>(`/api/planner/shared/${token}/`);
}

/**
 * A public itinerary by its slug — the `/plans/p/<slug>` page when no
 * published CommunityPlan holds the slug. Same payload as the token page
 * (`audience_gated`, never `share_token`); 404 for anything that is not a
 * public, unshadowed itinerary.
 */
export async function getPublicItineraryBySlug(slug: string): Promise<Itinerary> {
  return fetchApi<Itinerary>(`/api/planner/itineraries/by-slug/${encodeURIComponent(slug)}/`);
}

// === Trip Suggestions ===

export async function getTripSuggestions(limit?: number): Promise<{ suggestions: TripSuggestion[] }> {
  const query = limit !== undefined ? `?limit=${limit}` : '';
  return fetchApi<{ suggestions: TripSuggestion[] }>(`/api/planner/suggestions/${query}`);
}

export async function createTripFromSuggestion(
  suggestionId: string,
  startDate: string
): Promise<Itinerary> {
  return fetchApi<Itinerary>('/api/planner/trips/create-from-suggestion/', {
    method: 'POST',
    body: JSON.stringify({
      suggestion_id: suggestionId,
      start_date: startDate,
    }),
  });
}

// === Publish/Unpublish ===

export async function publishItinerary(
  id: number,
  publish: boolean
): Promise<{ message: string; itinerary: Itinerary; events_created?: number; events_deleted?: number }> {
  return fetchApi(`/api/planner/itineraries/${id}/publish/`, {
    method: 'POST',
    body: JSON.stringify({ publish }),
  });
}

// === Trip Briefing ===

export interface TripBriefingItem {
  type: string;
  message: string;
  urgency: string;
  trip_id: string;
  trip_title: string;
  destination: string;
  start_date: string;
  end_date: string;
  days_until_departure: number;
  current_trip_day: number | null;
  trip_duration_days: number;
  intel?: {
    trending_tag: string;
    intel_bullets: Array<{ icon?: string; text: string }>;
  };
}

export async function getTripBriefing(): Promise<TripBriefingItem[]> {
  return fetchApi('/api/v1/planner/trips/briefing/');
}

// === Pre-Trip Briefing ===

export interface PreTripBriefingSection {
  title: string;
  items: string[];
}

export interface PreTripBriefing {
  trip_id: string;
  title: string;
  destination: string;
  dates: string;
  start_date: string;
  end_date: string;
  days_until_departure: number;
  trip_duration_days: number;
  departing: Record<string, string | null> | null;
  returning: Record<string, string | null> | null;
  accommodations: Array<Record<string, unknown>>;
  while_away: Record<string, unknown>;
  todo_before: Record<string, unknown>;
  reservations: Array<Record<string, unknown>>;
  prose: Record<string, string>;
  is_full_briefing?: boolean;
}

export async function getPreTripBriefing(tripId: string): Promise<PreTripBriefing> {
  return fetchApi<PreTripBriefing>(`/api/v1/planner/trips/${tripId}/travel-brief/`);
}

// === Activity Verification ===

export interface VerificationResult {
  status: 'verified' | 'flagged' | null;
  reason: string;
  place_id: string;
}

// === Packing List ===

export interface PackingCategory {
  category: string;
  items: string[];
}

export async function getPackingList(itineraryId: number): Promise<{
  items: PackingCategory[];
  generated_at: string;
  cached: boolean;
}> {
  return fetchApi(`/api/v1/planner/itineraries/${itineraryId}/packing-list/`);
}

// Lifecycle endpoints
export async function setItineraryDates(
  id: number,
  startDate: string,
  endDate: string | undefined,
  expectedRevision: number,
): Promise<void> {
  await fetchApi(`/api/planner/itineraries/${id}/set-dates/`, {
    method: 'POST',
    body: JSON.stringify({
      start_date: startDate, expected_revision: expectedRevision,
      ...(endDate ? { end_date: endDate } : {}),
    }),
  });
}

export async function verifyActivity(
  name: string,
  location: string,
  itemId?: number
): Promise<VerificationResult> {
  return fetchApi<VerificationResult>('/api/v1/planner/verify-activity/', {
    method: 'POST',
    body: JSON.stringify({ name, location, item_id: itemId }),
  });
}

// === Lifecycle: Archive / Delete / Restore ===

export async function archiveItinerary(id: number): Promise<{ message: string }> {
  return fetchApi<{ message: string }>(`/api/planner/itineraries/${id}/archive/`, {
    method: 'POST',
  });
}

export async function unarchiveItinerary(id: number): Promise<{ message: string }> {
  return fetchApi<{ message: string }>(`/api/planner/itineraries/${id}/unarchive/`, {
    method: 'POST',
  });
}

export async function softDeleteItinerary(id: number): Promise<{ message: string }> {
  return fetchApi<{ message: string }>(`/api/planner/itineraries/${id}/delete/`, {
    method: 'POST',
  });
}

/** `publish` is optional: a rolling deploy can answer without it, and the
 *  caller must not throw when it is missing (`restoreNotice`). */
export interface RestoreItineraryResponse {
  id: number;
  lifecycle_state: string;
  publish?: PublishStatus;
}

export async function restoreItinerary(id: number): Promise<RestoreItineraryResponse> {
  return fetchApi<RestoreItineraryResponse>(`/api/planner/itineraries/${id}/restore/`, {
    method: 'POST',
  });
}

export async function hardDeleteItinerary(id: number): Promise<{ message: string }> {
  return fetchApi<{ message: string }>(`/api/planner/itineraries/${id}/hard-delete/`, {
    method: 'DELETE',
  });
}

export async function convertToRoutine(
  id: number, expectedRevision: number,
): Promise<{ message: string }> {
  return fetchApi<{ message: string }>(`/api/planner/itineraries/${id}/convert-to-routine/`, {
    method: 'POST', body: JSON.stringify({ expected_revision: expectedRevision }),
  });
}

export async function getSimilarItineraries(id: number): Promise<ItineraryListItem[]> {
  const response = await fetchApi<{ itineraries: ItineraryListItem[] }>(`/api/planner/itineraries/${id}/similar/`);
  return response.itineraries || [];
}

export async function getArchivedItineraries(): Promise<ItineraryListItem[]> {
  const response = await fetchApi<{ itineraries: ItineraryListItem[] }>('/api/planner/itineraries/archived/');
  return response.itineraries || [];
}

export async function getRecentlyDeletedItineraries(): Promise<ItineraryListItem[]> {
  const response = await fetchApi<{ itineraries: ItineraryListItem[] }>('/api/planner/itineraries/recently-deleted/');
  return response.itineraries || [];
}

// === Activity Photos ===

export async function uploadActivityPhoto(
  itineraryId: number,
  itemId: number,
  file: File,
  /**
   * True only once the user has been told "Anyone with this plan's link can
   * see this photo." and said yes. Required rather than defaulted, so a caller
   * has to state it: the server records consent from this field alone, and
   * without it keeps the photograph off the shared link.
   */
  audienceAcknowledged: boolean,
): Promise<{ photo_url: string }> {
  const formData = new FormData();
  formData.append('photo', file);
  if (audienceAcknowledged) formData.append('audience_acknowledged', 'true');
  const response = await fetch(`${API_BASE_URL}/api/planner/itineraries/${itineraryId}/items/${itemId}/photo/`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-CSRFToken': getCsrfToken() || '' },
    body: formData,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Upload failed');
  }
  return response.json();
}

/**
 * The owner lets a photograph that predates the share-page release onto their
 * shared link. Idempotent on the server. An upload never needs this: the
 * confirmation before it already said who can see the photo.
 */
export async function acknowledgeActivityPhotoShare(
  itineraryId: number,
  itemId: number,
  /** The photograph the owner was shown the note beside. The server refuses
      (409) if the activity now carries a different one. */
  photoUrl: string,
): Promise<{ acknowledged_at: string }> {
  return fetchApi(
    `/api/planner/itineraries/${itineraryId}/items/${itemId}/photo/acknowledge-share/`,
    { method: 'POST', body: JSON.stringify({ photo_url: photoUrl }) },
  );
}

export async function deleteActivityPhoto(itineraryId: number, itemId: number): Promise<void> {
  await fetchApi(`/api/planner/itineraries/${itineraryId}/items/${itemId}/photo/delete/`, {
    method: 'DELETE',
  });
}

// === Voting & Comments (Phase C-4) ===

export interface VoteSummary {
  interested: number;
  not_for_me: number;
}

function _getInviteToken(): string | null {
  try { return new URLSearchParams(window.location.search).get('invite'); } catch { return null; }
}

export async function voteItineraryItem(
  itineraryId: number,
  itemId: number,
  sentiment: 'interested' | 'not_for_me',
  sessionId?: string,
): Promise<{ vote_summary: VoteSummary; your_vote: string }> {
  const inviteToken = _getInviteToken();
  const url = `/api/planner/itineraries/${itineraryId}/items/${itemId}/vote/` + (inviteToken ? `?invite=${inviteToken}` : '');
  return fetchApi(url, {
    method: 'POST',
    body: JSON.stringify({ sentiment, session_id: sessionId }),
  });
}

export interface ActivityCommentData {
  id: number;
  text: string;
  author_name: string;
  session_id: string;
  created_at: string;
}

export async function listItemComments(
  itineraryId: number,
  itemId: number,
): Promise<{ comments: ActivityCommentData[] }> {
  const inviteToken = _getInviteToken();
  const url = `/api/planner/itineraries/${itineraryId}/items/${itemId}/comments/` + (inviteToken ? `?invite=${inviteToken}` : '');
  return fetchApi(url);
}

export async function addItemComment(
  itineraryId: number,
  itemId: number,
  text: string,
  sessionId?: string,
): Promise<ActivityCommentData> {
  const inviteToken = _getInviteToken();
  const url = `/api/planner/itineraries/${itineraryId}/items/${itemId}/comments/add/` + (inviteToken ? `?invite=${inviteToken}` : '');
  return fetchApi(url, {
    method: 'POST',
    body: JSON.stringify({ text, session_id: sessionId }),
  });
}

// === Invitations ===

export async function createInvitation(itineraryId: number): Promise<{ id: number; token: string; share_url: string; created_at: string }> {
  return fetchApi(`/api/planner/itineraries/${itineraryId}/create-invitation/`, {
    method: 'POST',
  });
}

export interface AddPodResult {
  pod_name: string;
  member_count: number;
  members_invited: number;
  already_invited: number;
}

export async function addPodToPlan(itineraryId: number, podId: string): Promise<AddPodResult> {
  return fetchApi<AddPodResult>(`/api/planner/itineraries/${itineraryId}/add-pod/`, {
    method: 'POST',
    body: JSON.stringify({ pod_id: podId }),
  });
}

export async function resolveInvitation(token: string): Promise<{
  plan_id: number;
  plan_slug: string;
  plan_title: string;
  destination: string;
  creator_name: string;
  accepted: boolean;
}> {
  return fetchApi(`/api/planner/invitations/${token}/resolve/`);
}

// === Visiting Context ===

export async function setVisitingContext(itineraryId: number, visitingContext: string): Promise<{ visiting_context: string }> {
  return fetchApi(`/api/planner/itineraries/${itineraryId}/visiting-context/`, {
    method: 'POST',
    body: JSON.stringify({ visiting_context: visitingContext }),
  });
}

// === Lock / Unlock ===

export async function lockPlan(itineraryId: number): Promise<{ locked_at: string }> {
  return fetchApi(`/api/planner/itineraries/${itineraryId}/lock/`, {
    method: 'POST',
  });
}

export async function unlockPlan(itineraryId: number): Promise<void> {
  await fetchApi(`/api/planner/itineraries/${itineraryId}/unlock/`, {
    method: 'POST',
  });
}

// === Recap Card ===

export async function generateRecap(id: number): Promise<void> {
  await fetchApi(`/api/planner/itineraries/${id}/generate-recap/`, { method: 'POST' });
}

export async function generateStory(
  id: number, expectedRevision: number,
): Promise<{ story: string; content_revision: number }> {
  return fetchApi(`/api/planner/itineraries/${id}/generate-story/`, {
    method: 'POST', body: JSON.stringify({ expected_revision: expectedRevision }),
  });
}
